/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable camelcase */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { CropsEnum, PlotType } from '@/api/rest/resources/types/plot';
import {
  CountryCodeNameMap,
  DEFAULT_PLOT_GROUP_FILTERS,
  DEFAULT_PLOT_GROUP_GROUP_BY,
  PlotGroup,
  PlotGroupFilters,
  PlotGroupFiltersEnum,
  PlotGroupGroupBy,
  PlotGroupGroupByEnum,
  PlotLabelName,
  PlotLabelValue,
} from '@/api/rest/resources/types/plotGroup';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useSetPlotGroupFilters } from '@/components/PlotGroups/hooks/useSetPlotGroup';
import { useActivePlotGroup, usePlotGroupNames } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { DialogWidgetHandle } from '@/components/widgets/o/Dialog/Dialog';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useAvailableFilters } from '@/pages/shared/hooks/useAvailableFilters';
import { usePlotLabelNames } from '@/pages/shared/hooks/usePlotLabels';
import { isNullish } from '@/utils/booleans';

export const usePlotGroupFiltersConfiguration = () => {
  const activePlotGroup = useActivePlotGroup();
  const { t } = useTranslation();

  const { data: availableFiltersData } = useAvailableFilters();
  const {
    available_country_codes = [],
    available_crops = [],
    available_land_types = [],
    has_deforestation = false,
  } = availableFiltersData ?? {};

  const availablePlotLabels: PlotLabelName[] = usePlotLabelNames().data ?? [];
  const activeFilters = useGetActivePlotGroup();

  const { setPlotGroupFilters } = useSetPlotGroupFilters();
  const dialogRef = useRef<DialogWidgetHandle>();

  const handleSetPlotGroupFilters = (
    formInput: PlotGroupFilters & PlotGroupGroupBy & { additional_plot_ids?: string[] },
  ) => {
    const filters = Object.fromEntries(
      Object.entries(formInput).filter(([key]) => Object.keys(PlotGroupFiltersEnum).includes(key)),
    ) as PlotGroupFilters;

    const groupBy = Object.fromEntries(
      Object.entries(formInput).filter(([key]) => Object.keys(PlotGroupGroupByEnum).includes(key)),
    ) as PlotGroupGroupBy;

    const { additional_plot_ids } = formInput;

    setPlotGroupFilters({ filters, groupBy, additional_plot_ids });
    dialogRef.current?.closeDialog();
  };

  const resetFormState = {
    ...DEFAULT_PLOT_GROUP_GROUP_BY,
    ...DEFAULT_PLOT_GROUP_FILTERS,
    additional_plot_ids: [],
  };

  const filtersWidgets = usePlotGroupFiltersWidgets({
    availablePlotLabels,
    available_country_codes,
    available_land_types,
    available_crops,
    has_deforestation,
  });

  return {
    name: DefaultWidgetNames.default_plot_group_filters,
    type: WidgetType.o_c_plot_group_filters,
    widgets: [
      {
        name: 'plot_group_filters_dialog',
        type: WidgetType.o_dialog,
        relatedData: {
          button: {
            icon: 'ri-filter-3-fill',
            text:
              activeFilters?.length > 0
                ? `${t('global.widget.filters.button.filter')} (${activeFilters.length})`
                : t('global.widget.filters.button.filter'),
            variant: 'outline',
            className: 'w-fit',
          },
          ref: dialogRef,
        },
        visible: true,
        widgets: [
          {
            name: 'plot_group_filters_dialog_content',
            type: WidgetType.o_dialog_content,
            visible: true,
            relatedData: {
              withXButton: false,
              className: 'py-4 px-6',
            },
            widgets: [
              {
                name: 'plot_group_filters_form',
                type: WidgetType.o_form,
                visible: true,
                relatedData: {
                  defaultValues: {
                    ...activePlotGroup.filters,
                    ...activePlotGroup.groupBy,
                    additional_plot_ids: activePlotGroup.additional_plot_ids ?? [],
                  },
                  onSubmit: handleSetPlotGroupFilters,
                },
                widgets: [
                  {
                    name: 'plot_group_filters_dialog_header',
                    type: WidgetType.o_dialog_header,
                    visible: true,
                    relatedData: {
                      className: 'flex flex-row justify-between items-center border-b border-divider mb-0',
                    },
                    widgets: [
                      {
                        name: 'plot_group_filters_dialog_header_text',
                        type: WidgetType.a_text,
                        visible: true,
                        relatedData: {
                          text: t('global.widget.filters.dialog.filterBy'),
                          className: 'typography-body1Semibold',
                        },
                      },
                      {
                        name: 'plot_group_filters_dialog_header_text_close',
                        type: WidgetType.o_dialog_close,
                        visible: true,
                        relatedData: {
                          className: 'pr-0',
                        },
                      },
                    ],
                  },
                  {
                    name: 'plot_group_filters_dialog_body',
                    type: WidgetType.o_dialog_body,
                    visible: true,
                    relatedData: {
                      className: 'max-h-[65vh] overflow-y-auto',
                    },
                    widgets: filtersWidgets,
                  },
                  {
                    name: 'plot_group_filters_dialog_footer',
                    type: WidgetType.o_dialog_footer,
                    visible: true,
                    widgets: [
                      {
                        name: 'plot_group_filters_dialog_footer_stack_container',
                        type: WidgetType.a_stack,
                        relatedData: {
                          className: 'w-full justify-between pt-4',
                          direction: 'row',
                          centerMain: true,
                          spacing: 4,
                        },
                        visible: true,
                        widgets: [
                          {
                            name: 'plot_group_filters_dialog_footer_clear_filters_button',
                            type: WidgetType.o_form_clear_button,
                            visible: true,
                            relatedData: {
                              text: t('global.widget.filters.button.clearFilters'),
                              type: 'button',
                              variant: 'text',
                              className: 'w-fit aspect-auto',
                              resetValues: resetFormState,
                            },
                          },
                          {
                            name: 'plot_group_filters_dialog_footer_submit_button',
                            type: WidgetType.o_form_submit_button,
                            visible: true,
                            relatedData: {
                              text: t('global.widget.filters.button.applyFilters'),
                              className: 'w-fit',
                              shouldSubmit: activeFilters.length > 0,
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  } as WidgetSlotConfiguration;
};

export const usePlotGroupFiltersWidgets = ({
  availablePlotLabels,
  available_country_codes,
  available_land_types,
  available_crops,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  has_deforestation, // TODO: MVP-8557 remove lint disable
}: {
  availablePlotLabels: PlotLabelName[];
  available_country_codes: string[];
  available_land_types: PlotType[];
  available_crops: CropsEnum[];
  has_deforestation: boolean;
}) => {
  const { t } = useTranslation();
  const showGroupByOptions = !useUIStyle(OrganizationUIStyle.CONSERVATION);

  return [
    {
      name: 'plot_group_filters_dialog_body_section_date_range_wrapper',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: {
        className: 'border-b border-divider py-4',
      },
      widgets: [
        {
          name: 'plot_group_filters_dialog_body_section_date_range_wrapper_title',
          type: WidgetType.a_text,
          visible: true,
          relatedData: {
            text: t('global.widget.filters.sections.dateRange'),
            className: 'uppercase typography-overline py-4 font-semibold',
          },
        },
        {
          name: 'plot_group_filters_dialog_body_section_date_range_wrapper_date_forms',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: {
            direction: 'row',
            spacing: 8,
            className: 'w-full',
          },
          widgets: [
            {
              name: 'plot_group_filters_dialog_body_section_start_date',
              type: WidgetType.o_form_section,
              visible: true,
              relatedData: {
                spacing: 2,
                className: 'flex-1',
                section: {
                  className: 'w-full',
                  centerMain: true,
                  direction: 'row',
                  spacing: 4,
                },
                formName: PlotGroupFiltersEnum.start_date,
              },
              widgets: [
                {
                  name: 'plot_group_filters_dialog_body_section_start_date_button',
                  type: WidgetType.o_form_calendar,
                  visible: true,
                  relatedData: {
                    formName: PlotGroupFiltersEnum.start_date,
                    placeholder: t('global.widget.filters.sections.start_date.placeholder'),
                    maxFormName: PlotGroupFiltersEnum.end_date,
                  },
                },
              ],
            },
            {
              name: 'plot_group_filters_dialog_body_section_end_date',
              type: WidgetType.o_form_section,
              visible: true,
              relatedData: {
                spacing: 2,
                className: 'flex-1',
                section: {
                  className: 'w-full',
                  centerMain: true,
                  direction: 'row',
                  spacing: 4,
                },
                formName: PlotGroupFiltersEnum.end_date,
              },
              widgets: [
                {
                  name: 'plot_group_filters_dialog_body_section_end_date_button',
                  type: WidgetType.o_form_calendar,
                  visible: true,
                  relatedData: {
                    formName: PlotGroupFiltersEnum.end_date,
                    placeholder: t('global.widget.filters.sections.end_date.placeholder'),
                    minFormName: PlotGroupFiltersEnum.start_date,
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      name: 'plot_group_filters_dialog_body_section_plot_labels',
      type: WidgetType.o_form_section,
      visible: availablePlotLabels.length > 0 && showGroupByOptions,
      relatedData: {
        previewChildrenLimit: 8,
        spacing: 2,
        className: 'py-4 border-b border-divider',
        label: {
          text: t('global.widget.filters.sections.labels.title'),
          className: 'uppercase typography-overline py-2 font-semibold',
        },
        section: {
          className: 'w-fit flex-wrap',
          centerMain: true,
          direction: 'row',
          spacing: 2,
        },
        formName: PlotGroupGroupByEnum.labels,
      },
      widgets: [
        {
          name: 'plot_group_filters_dialog_body_section_plot_labels_form',
          visible: true,
          type: WidgetType.o_form_button,
          relatedData: {
            data: availablePlotLabels.map((label) => ({
              text: getValueStringMapping(label.name, PlotGroupGroupByEnum.labels),
              formName: PlotGroupGroupByEnum.labels,
              value: label,
            })),
          },
          repeat: {
            data: 'relatedData.data',
            key: 'value',
            template: {
              name: 'plot_group_filters_dialog_body_section_plot_labels_button',
              type: WidgetType.o_form_button,
              visible: true,
              relatedData: {
                preventMultipleSelection: true, // This should only be temporal, until the BE accepts multiple labels
                variant: 'outline',
                className: 'w-fit whitespace-nowrap',
              },
            },
          },
        },
      ],
    },
    {
      name: 'plot_group_filters_dialog_body_section_plot_labels_values',
      type: WidgetType.o_c_form_label_values_section,
      visible: availablePlotLabels.length > 0 && showGroupByOptions,
    },
    {
      name: 'plot_group_filters_dialog_body_section_country',
      type: WidgetType.o_form_section,
      visible: available_country_codes.length > 0,
      relatedData: {
        previewChildrenLimit: 8,
        spacing: 2,
        className: 'py-4 border-b border-divider',
        label: {
          text: t('global.widget.filters.sections.country.title'),
          className: 'uppercase typography-overline py-2 font-semibold',
        },
        section: {
          className: 'w-fit flex-wrap justify-evenly-start',
          centerMain: true,
          direction: 'row',
          spacing: 4,
        },
        formName: PlotGroupFiltersEnum.country,
      },
      widgets: [
        {
          name: 'plot_group_filters_dialog_body_section_country_checkbox',
          type: WidgetType.o_form_checkbox,
          visible: true,
          relatedData: {
            data: available_country_codes.map((countryCode: string) => ({
              text: getValueStringMapping(countryCode, PlotGroupFiltersEnum.country),
              formName: PlotGroupFiltersEnum.country,
              value: getValueStringMapping(countryCode, PlotGroupFiltersEnum.country),
            })),
          },
          repeat: {
            data: 'relatedData.data',
            key: 'value',
            template: {
              name: 'plot_group_filters_dialog_body_section_country_button',
              type: WidgetType.o_form_checkbox,
              visible: true,
              relatedData: {},
            },
          },
        },
      ],
    },
    {
      name: 'plot_group_filters_dialog_body_section_type_of_land',
      type: WidgetType.o_form_section,
      visible: available_land_types.length > 0,
      relatedData: {
        previewChildrenLimit: 4,
        spacing: 2,
        className: 'py-4 border-b border-divider',
        label: {
          text: t('global.widget.filters.sections.plot__type.title'),
          className: 'uppercase typography-overline py-2 font-semibold',
        },
        section: {
          className: 'w-fit flex-wrap justify-evenly-start',
          centerMain: true,
          direction: 'row',
          spacing: 4,
        },
        formName: PlotGroupFiltersEnum.plot__type,
      },
      widgets: [
        {
          name: 'plot_group_filters_dialog_body_section_type_land_checkbox_section',
          type: WidgetType.o_form_checkbox,
          visible: true,
          relatedData: {
            data: available_land_types.map((crop) => ({
              i18nPath: getValueStringMapping(crop, PlotGroupFiltersEnum.plot__type),
              formName: PlotGroupFiltersEnum.plot__type,
              value: crop,
            })),
          },
          repeat: {
            data: 'relatedData.data',
            key: 'value',
            template: {
              name: 'plot_group_filters_dialog_body_section_type_land_checkbox',
              type: WidgetType.o_form_checkbox,
              visible: true,
              relatedData: {},
            },
          },
        },
      ],
    },
    {
      name: 'plot_group_filters_dialog_body_section_land_use',
      type: WidgetType.o_form_section,
      visible: available_crops.length > 0,
      relatedData: {
        previewChildrenLimit: 8,
        spacing: 2,
        className: 'py-4 border-b border-divider',
        label: {
          text: t('global.widget.filters.sections.plot__crops.title'),
          className: 'uppercase typography-overline py-2 font-semibold',
        },
        section: {
          className: 'w-fit flex-wrap justify-evenly-start',
          centerMain: true,
          direction: 'row',
          spacing: 4,
        },
        formName: PlotGroupFiltersEnum.plot__crops,
      },
      widgets: [
        {
          name: 'plot_group_filters_dialog_body_section_land_use_checkbox',
          type: WidgetType.o_form_checkbox,
          visible: true,
          relatedData: {
            data: available_crops.map((crop) => ({
              i18nPath: getValueStringMapping(crop, PlotGroupFiltersEnum.plot__crops),
              formName: PlotGroupFiltersEnum.plot__crops,
              value: crop,
            })),
          },
          repeat: {
            data: 'relatedData.data',
            key: 'value',
            template: {
              name: 'plot_group_filters_dialog_body_section_land_use_button',
              type: WidgetType.o_form_checkbox,
              visible: true,
              relatedData: {},
            },
          },
        },
      ],
    },
    {
      name: 'plot_group_filters_dialog_body_section_deforestation',
      type: WidgetType.o_form_section,
      visible: false, // TODO: MVP-8557 unhide deforestation cards for agriculture (use: has_deforestation)
      relatedData: {
        spacing: 2,
        className: 'py-4 border-b border-divider',
        label: {
          i18nPath: 'global.widget.filters.sections.deforested.title',
          className: 'uppercase typography-overline py-2 font-semibold',
        },
        section: {
          className: 'w-fit flex-wrap justify-evenly-start',
          centerMain: true,
          direction: 'row',
          spacing: 4,
        },
        formName: PlotGroupFiltersEnum.deforested,
      },
      widgets: [
        {
          name: `deforestation_radio_group`,
          type: WidgetType.o_form_radio_group,
          visible: true,
          relatedData: {
            name: PlotGroupFiltersEnum.deforested,
            className: 'flex-row w-full gap-16',
          },
          widgets: [
            {
              name: `filters_dialog_body_section_deforestation_true`,
              type: WidgetType.o_form_radio_item,
              visible: true,
              relatedData: {
                data: [
                  {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: t(getValueStringMapping('true', PlotGroupFiltersEnum.deforested) as any),
                    value: true,
                  },
                  {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: t(getValueStringMapping('false', PlotGroupFiltersEnum.deforested) as any),
                    value: false,
                  },
                ],
              },
              repeat: {
                data: 'relatedData.data',
                key: 'value',
                template: {
                  name: 'filters_dialog_body_section_deforestation_item',
                  type: WidgetType.o_form_radio_item,
                  visible: true,
                  relatedData: {
                    formName: PlotGroupFiltersEnum.deforested,
                  },
                },
              },
            },
          ],
        },
      ],
    },
    // TODO: MVP-CW - Uncomment and implement these fields when needed
    // {
    //   name: 'plot_group_filters_dialog_body_section_risks',
    //   type: WidgetType.o_form_section,
    //   visible: true,
    //   relatedData: {
    //     spacing: 2,
    //     className: 'py-4 border-b border-divider',
    //     label: {
    //       text: t('global.widget.filters.sections.risks.title'),
    //       className: 'uppercase typography-overline py-2 font-semibold',
    //     },
    //     section: {
    //       className: 'w-fit',
    //       centerMain: true,
    //       direction: 'row',
    //       spacing: 2,
    //     },
    //     formName: PlotGroupFiltersEnum.risks,
    //   },
    //   widgets: [
    //     {
    //       name: 'plot_group_filters_dialog_body_section_risk_button',
    //       visible: true,
    //       type: WidgetType.o_form_button,
    //       relatedData: {
    //         data: Object.values(RiskLevels).map((risk) => ({
    //           i18nPath: getValueStringMapping(risk, PlotGroupFiltersEnum.risks),
    //           formName: PlotGroupFiltersEnum.risks,
    //           value: risk,
    //         })),
    //       },
    //       repeat: {
    //         data: 'relatedData.data',
    //         key: 'value',
    //         template: {
    //           name: 'plot_group_filters_dialog_body_section_risk_button',
    //           type: WidgetType.o_form_button,
    //           visible: true,
    //           relatedData: {
    //             variant: 'outline',
    //             className: 'w-fit',
    //           },
    //         },
    //       },
    //     },
    //   ],
    // },
    // TODO: MVP-CW - Uncomment and implement these fields when needed
    // {
    //   name: 'plot_group_filters_dialog_body_section_resilience',
    //   type: WidgetType.o_form_section,
    //   visible: true,
    //   relatedData: {
    //     spacing: 2,
    //     className: 'py-4 border-b border-divider',
    //     label: {
    //       text: t('global.widget.filters.sections.resiliency.title'),
    //       className: 'uppercase typography-overline py-2 font-semibold',
    //     },
    //     section: {
    //       className: 'w-fit',
    //       centerMain: true,
    //       direction: 'row',
    //       spacing: 2,
    //     },
    //     formName: PlotGroupFiltersEnum.resilience,
    //   },
    //   widgets: [
    //     {
    //       name: 'plot_group_filters_dialog_body_section_resilience_button',
    //       type: WidgetType.o_form_button,
    //       visible: true,
    //       relatedData: {
    //         data: Object.values(ResiliencyTrend).map((trend) => ({
    //           i18nPath: getValueStringMapping(trend, PlotGroupFiltersEnum.resilience),
    //           formName: PlotGroupFiltersEnum.resilience,
    //           value: trend,
    //         })),
    //       },
    //       repeat: {
    //         data: 'relatedData.data',
    //         key: 'value',
    //         template: {
    //           name: 'plot_group_filters_dialog_body_section_resilience_button',
    //           type: WidgetType.o_form_button,
    //           visible: true,
    //           relatedData: {
    //             variant: 'outline',
    //             className: 'w-fit',
    //           },
    //         },
    //       },
    //     },
    //   ],
    // },
    {
      name: 'plot_group_filters_dialog_body_section_plots',
      type: WidgetType.o_c_form_plots_section,
      visible: true,
    },
  ];
};

export const usePlotGroupFiltersManagementPanel = ({ canEditFilters }: { canEditFilters: boolean }) => {
  const activePlotGroup = useActivePlotGroup();
  const { filters, groupBy, additional_plot_ids = [] } = activePlotGroup;

  const { setPlotGroupFilters, setDefaultPlotGroup } = useSetPlotGroupFilters();

  const handleRemoveFilterOrGroup = (
    key: keyof typeof PlotGroupFiltersEnum | keyof typeof PlotGroupGroupByEnum,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) => {
    const newFilters = { ...filters };
    const newGroupBy = { ...groupBy };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (Object.values(PlotGroupGroupByEnum).includes(key as any)) {
      newGroupBy[`${key as keyof typeof PlotGroupGroupByEnum}`] = null;
      setPlotGroupFilters({ filters: newFilters, groupBy: newGroupBy });
      return;
    }

    const filtersKey = key as keyof typeof PlotGroupFiltersEnum;

    if (Array.isArray(newFilters[`${filtersKey}`])) {
      const currentValues = newFilters[`${filtersKey}`] as unknown[];
      (newFilters as Record<string, unknown>)[`${filtersKey}`] = currentValues.filter((v) => v !== value);
    } else {
      (newFilters as Record<string, unknown>)[`${filtersKey}`] = undefined;
    }

    setPlotGroupFilters({ filters: newFilters, groupBy: newGroupBy });
  };

  const handleRemoveAdditionalPlot = (plotId: string) => {
    const newAdditionalPlotIds = additional_plot_ids.filter((id) => id !== plotId);
    setPlotGroupFilters({ filters, groupBy, additional_plot_ids: newAdditionalPlotIds });
  };

  return usePlotGroupFiltersPills({
    plotGroup: activePlotGroup,
    canEditFilters,
    onClear: setDefaultPlotGroup,
    handleRemoveAdditionalPlot,
    handleRemoveFilterOrGroup,
  });
};

export const usePlotGroupFiltersPills = ({
  plotGroup,
  filtersArg,
  canEditFilters = true,
  onClear,
  handleRemoveAdditionalPlot,
  handleRemoveFilterOrGroup,
  styles,
}: {
  plotGroup: PlotGroup;
  filtersArg?: Array<{
    key: keyof typeof PlotGroupFiltersEnum | keyof typeof PlotGroupGroupByEnum;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
  }>;
  canEditFilters?: boolean;
  onClear?: () => void;
  handleRemoveAdditionalPlot?: (plotId: string) => void;
  handleRemoveFilterOrGroup?: (
    key: keyof typeof PlotGroupFiltersEnum | keyof typeof PlotGroupGroupByEnum,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) => void;
  styles?: {
    container?: string;
  };
}) => {
  const { t } = useTranslation();
  const { DEFAULT, NO_GROUPING, DETAILS } = usePlotGroupNames();

  const { name, additional_plot_ids = [] } = plotGroup;
  const activeAdditionalPlotsFilter = additional_plot_ids.length > 0;
  const activeFilters = getPlotGroupFilter(plotGroup);
  const filters = filtersArg ?? activeFilters;

  const filterPills = filters?.map(({ key, value }) => {
    // TODO: MVP-5966 countries need to be translation
    const notTranslatedKeys = [
      PlotGroupFiltersEnum.country,
      PlotGroupGroupByEnum.labels,
      PlotGroupFiltersEnum.plot__labels__id,
      PlotGroupFiltersEnum.start_date,
      PlotGroupFiltersEnum.end_date,
    ];
    const valueText = notTranslatedKeys.includes(key as PlotGroupFiltersEnum)
      ? getValueStringMapping(value, key)
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t(getValueStringMapping(value, key) as any);

    return {
      name: `plot_group_filters_management_panel_pill_${key}_${value}`,
      type: WidgetType.a_pill,
      visible: filters?.length > 0 || activeAdditionalPlotsFilter,
      relatedData: {
        className: 'bg-bg-light-grey typography-body2 text-text-secondary',
      },
      widgets: [
        {
          name: `plot_group_filters_management_panel_pill_text_${key}_${value}`,
          type: WidgetType.a_text,
          visible: true,
          relatedData: {
            // eslint-disable-next-line sonarjs/no-nested-template-literals
            text: `${t(`global.widget.filters.sections.${key}.title`, { defaultValue: key })}: ${valueText}`,
          },
        },
        {
          name: `plot_group_filters_management_panel_pill_button_${key}_${value}`,
          type: WidgetType.a_button,
          visible: canEditFilters,
          relatedData: {
            variant: 'text',
            className: 'w-fit p-0 bg-active-54 text-bg-light-grey hover:text-active-54',
            size: 'small',
            onClick: handleRemoveFilterOrGroup
              ? () => handleRemoveFilterOrGroup(key as keyof PlotGroupFilters | PlotGroupGroupByEnum, value)
              : undefined,
          },
          widgets: [
            {
              name: `plot_group_filters_management_panel_pill_button_icon_${key}_${value}`,
              type: WidgetType.a_icon,
              visible: !isNullish(handleRemoveFilterOrGroup),
              relatedData: {
                icon: 'ri-close-line',
                className: '',
              },
            },
          ],
        },
      ],
    };
  });

  const recommendationCardPill = {
    name: 'plot_group_filters_management_panel_recommendation_card_pill',
    type: WidgetType.a_pill,
    visible:
      filters?.length === 0 && !activeAdditionalPlotsFilter && name && ![DEFAULT, NO_GROUPING, DETAILS].includes(name),
    relatedData: {
      className: 'bg-bg-light-grey typography-body2 text-text-secondary',
    },
    widgets: [
      {
        name: `plot_group_filters_management_panel_pill_recommendation_card_text`,
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: name,
        },
      },
      {
        name: 'plot_group_filters_management_panel_pill_recommendation_card_button',
        type: WidgetType.a_button,
        visible: canEditFilters,
        relatedData: {
          variant: 'text',
          className: 'w-fit p-0 bg-active-54 text-bg-light-grey hover:text-active-54',
          size: 'small',
          onClick: onClear,
        },
        widgets: [
          {
            name: 'plot_group_filters_management_panel_pill_recommendation_card_button_icon',
            type: WidgetType.a_icon,
            visible: true,
            relatedData: {
              icon: 'ri-close-line',
            },
          },
        ],
      },
    ],
  };

  return {
    name: DefaultWidgetNames.default_plot_group_filters_management,
    type: WidgetType.o_c_plot_group_filters_management,
    widgets: [
      {
        name: 'o_plot_group_filters_management_stack_container',
        type: WidgetType.a_stack,
        relatedData: {
          className: styles?.container ?? 'flex-wrap py-2',
          direction: 'row',
          centerMain: true,
          spacing: 3,
        },
        visible: true,
        widgets: [
          ...filterPills,
          {
            name: 'plot_group_filters_management_panel_pill_additional_plots',
            type: WidgetType.o_c_plot_group_additional_plots_filter_pills,
            visible: activeAdditionalPlotsFilter,
            relatedData: {
              plotGroup,
              onRemove: canEditFilters ? handleRemoveAdditionalPlot : undefined,
            },
          },
          recommendationCardPill,
          {
            name: 'plot_group_filters_management_panel_pill_button_clear_all',
            type: WidgetType.a_button,
            visible: ((activeFilters && activeFilters?.length > 0) || activeAdditionalPlotsFilter) && canEditFilters,
            relatedData: {
              variant: 'text',
              className: 'w-fit underline p-2',
              text: t('global.widget.filters.button.clearAll'),
              size: 'small',
              onClick: onClear,
            },
          },
        ],
      },
    ],
  } as WidgetSlotConfiguration;
};

export const useGetActivePlotGroup = () => {
  const activePlotGroup = useActivePlotGroup();

  return getPlotGroupFilter(activePlotGroup);
};

export const getPlotGroupFilter = (plotGroup: PlotGroup) => {
  const { filters, groupBy } = plotGroup;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeFilters = Object.entries(filters).reduce<Array<{ key: string; value: any }>>((acc, [key, value]) => {
    const isArrayValue = Array.isArray(value);

    if (isArrayValue && value.length === 0) {
      return acc;
    }

    if (!isArrayValue) {
      if (isNullish(value)) return acc;

      return [...acc, { key, value }];
    }

    return [
      ...acc,
      ...value.map((v) => ({
        key,
        value: v,
      })),
    ];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, []) as { key: keyof typeof PlotGroupFiltersEnum; value: any }[];

  const activeGroupBy = Object.entries(groupBy).reduce<Array<{ key: string; value: string | null }>>(
    (acc, [key, value]) => {
      if (isNullish(value) || isNullish(value.name)) return acc;

      return [...acc, { key, value: value.name }];
    },
    [],
  ) as { key: keyof typeof PlotGroupGroupByEnum; value: string }[];

  return [...activeFilters, ...activeGroupBy];
};

const getValueStringMapping = (value: string | PlotLabelValue, key: string) => {
  switch (key) {
    case PlotGroupFiltersEnum.plot__crops:
      return `global.crops.${value}`;
    case PlotGroupFiltersEnum.plot__type:
      return `global.plotTypes.${value}`;
    case PlotGroupFiltersEnum.risks:
      return `global.widget.filters.sections.risks.${value}`;
    case PlotGroupFiltersEnum.resilience:
      return `global.widget.filters.sections.resiliency.${value}`;
    case PlotGroupFiltersEnum.country:
      // TODO: MVP-5966 Add translation for country names
      return CountryCodeNameMap[value as keyof typeof CountryCodeNameMap] || value;
    case PlotGroupGroupByEnum.labels:
      return value;
    case PlotGroupFiltersEnum.plot__labels__id:
      return (value as PlotLabelValue).value;
    case PlotGroupFiltersEnum.start_date:
      return new Date(value as string).toLocaleDateString();
    case PlotGroupFiltersEnum.end_date:
      return new Date(value as string).toLocaleDateString();
    case PlotGroupFiltersEnum.deforested:
      return `global.widget.filters.sections.deforested.value.${value}`;
    default:
      return value;
  }
};
